import React, {useState} from 'react';
import Section, {Sections} from '../../Components/Containers/Section';
import Money from '../../Components/Formatting/Money';
import NoItems from '../../Components/EmptyStates/NoItems';
import EditEstimate from './EditEstimate';
import Button from '../../Components/Button/Button';
import { ulid } from 'ulid';
import { Link } from 'react-router-dom';
import useItemisation from '../../Data/Itemisations';
import Icon from '../../Components/Icons/Icon';
import { useRedirect } from '../../Services/helpers';

const Estimate = ({quote, refresh, updateEstimate, createItemisation}) => {
    const [givingSimpleEstimate, setGivingSimpleEstimate] = useState(false);

    return <div>
        <Sections>
            <Section title="Simple Estimate">
                <SimpleEstimate quote={quote} setGivingSimpleEstimate={setGivingSimpleEstimate} />
            </Section>
            <Section title="Cost Breakdowns">
                <CostBreakdowns quote={quote} createItemisation={createItemisation} />
            </Section>
        </Sections>

        <EditEstimate estimate={quote.estimate ?? {}}
            isOpen={givingSimpleEstimate}
            setIsOpen={setGivingSimpleEstimate}
            save={updateEstimate}
            onSave={refresh}
        />
    </div>
}

const SimpleEstimate = ({quote, setGivingSimpleEstimate}) => {
    if (quote.estimate) {
        return <>
            <p>
                <Money pence={quote.estimate.lower} /> {quote.estimate.upper && <>- <Money pence={quote.estimate.upper} /></>}
                <Button onClick={() => setGivingSimpleEstimate(true)}
                    theme="light"
                    icon="Pencil"
                    label="Edit"
                    className="float-right"
                    size="sm"
                    zeroMargin
                />
            </p>
        </>
    }

    return <NoItems icon="BullseyeArrow"
        title="Provide simple estimate"
        message="Give a single price or an upper/lower cost estimate."
        onClick={() => setGivingSimpleEstimate(true)}
        short
    />
}

const CostBreakdowns = ({quote, createItemisation}) => {
    const {go, Redirect} = useRedirect();

    const createItemisationAndGo = () => {
        const id = ulid();
        return createItemisation({id}).then(() => go(`/manage/quotes/${quote.id}/itemisations/${id}`));
    }

    return <>
        <ul className="divide-y divide-gray-200 mt-0 mb-6 first:pt-0">
            {quote.itemisationIds && quote.itemisationIds.map(
                (itemisationId) => <Itemisation key={itemisationId} quoteId={quote.id} itemisationId={itemisationId} />
            )}
        </ul>
        <NoItems icon="ListCheck"
            title="Add a cost breakdown"
            message="Break down the cost of this quote into categories and items."
            onClick={createItemisationAndGo}
            short
        />

        <Redirect />
    </>
}

const Itemisation = ({quoteId, itemisationId}) => {
    const {itemisation} = useItemisation(itemisationId);

    const summarise = () => {
        if (!itemisation) {
            return <span className="text-muted">Loading...</span>;
        }

        if (!itemisation.categories?.length) {
            return <span className="text-muted">No categories</span>;
        }

        return <ul>
            {itemisation.categories.map((category) => {
                return <li key={category.id} className="text-nowrap after:content-[','] last:after:content-['']">
                    {category.name}
                </li>
            })}
        </ul>
    }

    return <div className="flex py-3">
        <div className="grow">
            {summarise()}
            {itemisation && itemisation?.draft && <p className="text-brand-secondary text-xs font-semibold">
                Draft - not visible to customer
            </p>}
        </div>
        <div className="shrink-0 px-2">
            <Money pence={itemisation?.total ?? 0} />
        </div>
        <div className="shrink-0">
            <Link to={`/manage/quotes/${quoteId}/itemisations/${itemisationId}`} className="btn btn-light btn-sm">
                <Icon icon="Pencil" className="mr-2" size="0.5x" />
                Edit
            </Link>
        </div>
    </div>
}

export default Estimate;
